import { cs } from '../../lib/currency';
/**
 * iter307cz — Role-agnostic Credit Ledger section.
 * iter307da — Adds the "Adjust" capability (credit/debit) so admins
 *             can manually post a credit movement on any user
 *             (parent / customer / coach / staff), with reason +
 *             optimistic refresh, same as the parent profile.
 *
 * Endpoint reads : GET /api/admin/credit-ledger/user/:user_id/credit-history
 * Endpoint adjusts: POST /api/admin/customers/:user_id/credit/adjust
 *                  (the existing endpoint is keyed on user_id, role-agnostic)
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Loader2, Plus, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const CreditLedgerSection = ({ userId, title = 'Credit Ledger', description }) => {
  const [balance, setBalance] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/credit-ledger/user/${userId}/credit-history`);
      setBalance(Number(data?.balance || 0));
      setRows(data?.ledger_history || []);
    } catch (e) {
      setBalance(0);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  const submitAdjust = async () => {
    const amount = parseFloat(adjustAmount);
    if (Number.isNaN(amount) || amount === 0) {
      toast.error('Enter a non-zero amount');
      return;
    }
    if (!adjustReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.post(`/admin/customers/${userId}/credit/adjust`, {
        amount,
        reason: adjustReason.trim(),
      });
      const sign = amount > 0 ? 'Credited' : 'Debited';
      toast.success(`${sign} ${cs()}${Math.abs(amount).toFixed(2)} — new balance ${cs()}${Number(data?.balance || 0).toFixed(2)}`);
      setAdjustOpen(false);
      setAdjustAmount('');
      setAdjustReason('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust credit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card data-testid="credit-ledger-section">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                {title}
                <span className="text-sm font-normal text-slate-500 ml-2">
                  Balance: <span className="font-semibold text-emerald-600" data-testid="credit-ledger-section-balance">
                    {cs()}{balance.toFixed(2)}
                  </span>
                </span>
              </CardTitle>
              <CardDescription>
                {description || "Every credit issued or applied to this account. Cancellations with \"Account credit\" mode appear here automatically."}
              </CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => {
                setAdjustAmount('');
                setAdjustReason('');
                setAdjustOpen(true);
              }}
              data-testid="credit-ledger-section-adjust"
            >
              <Plus className="h-4 w-4" />
              Adjust
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-500 italic" data-testid="credit-ledger-section-empty">
              No credit activity yet.
            </p>
          ) : (
            <div className="divide-y divide-slate-100 -mx-6">
              {rows.map((row) => {
                const delta = Number(row.delta || 0);
                const isCredit = delta > 0;
                const label = row.reason || row.note
                  || `${(row.entity_type || 'account').replace(/_/g, ' ')}`;
                return (
                  <div
                    key={row.id}
                    className="px-6 py-3 flex items-start justify-between gap-4"
                    data-testid={`credit-ledger-section-row-${row.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {(row.created_at || '').slice(0, 19).replace('T', ' ')}
                        {row.actor_name && ` · by ${row.actor_name}`}
                        {row.entity_type && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">
                            {String(row.entity_type).replace(/_/g, ' ')}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className={`text-sm font-semibold whitespace-nowrap ${isCredit ? 'text-emerald-600' : 'text-orange-600'}`}>
                      {isCredit ? '+' : '−'}{cs()}{Math.abs(delta).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Adjust dialog */}
      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Account Credit</DialogTitle>
            <DialogDescription>
              Positive amount credits the account, negative debits.
              All adjustments are audited to the credit ledger.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="e.g. 50 or -25"
                data-testid="credit-ledger-section-adjust-amount"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Positive = credit. Negative = debit. Current balance: <strong>{cs()}{balance.toFixed(2)}</strong>
              </p>
            </div>
            <div>
              <Label>Reason <span className="text-red-500">*</span></Label>
              <Textarea
                rows={3}
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder="Why is this credit being adjusted?"
                data-testid="credit-ledger-section-adjust-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustOpen(false)}>Cancel</Button>
            <Button
              onClick={submitAdjust}
              disabled={saving}
              data-testid="credit-ledger-section-adjust-save"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Apply Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreditLedgerSection;
